import { EFinancingProgram, EPlanType, EWorksheetDecision } from '@if/core/types'
import { ErrorOutline } from '@mui/icons-material'
import { Grid, Stack, useTheme } from '@mui/material'
import { Breadcrumb, InputTextField, PageError, PageSpinner } from '@src/components'
import AvailableCreditTooltip from '@src/components/AvailableCreditTooltip'
import PendingMessage from '@src/components/PendingMessage'
import PrivateMessage from '@src/containers/ViewCreditApplicationPage/components/privateMessage'
import WarningMessageAccordion from '@src/containers/ViewCreditApplicationPage/components/warningMessageAccordion'
import { TRootState, useAppDispatch, useAppSelector } from '@src/data/store'
import { appSelectors } from '@src/data/store/AppStore'
import { availableCreditSelectors } from '@src/data/store/AvailableCredit/available-credit-store'
import { creditSelectors } from '@src/data/store/CreditApplication'
import { documentSelectors } from '@src/data/store/Document'
import { useSideEffect } from '@src/data/store/effects/side-effects'
import { fullCreditApplicationEffects } from '@src/data/store/FullCreditApplication'
import ifXpressWorksheetEffects from '@src/data/store/IfXpressWorksheet/ifxpress-worksheet-effects'
import {
  ifXpressWorksheetActions,
  ifXpressWorksheetSelectors,
} from '@src/data/store/IfXpressWorksheet/ifxpress-worksheet-store'
import { messageEffects, messageSelectors } from '@src/data/store/Message'
import {
  BaseWorksheetFundingThresholdExtend,
  CreditApplicationMessage,
  NormsMessage,
  WorksheetDecision,
} from '@src/data/types'
import { FundingInfoDto } from '@src/data/types/FundingInfoDto'
import { IfXpressWorksheetDecisionDTO } from '@src/data/types/IfXpressWorksheetSchema'
import { reportErrorToConsole } from '@src/services/error-logger'
import { nanoid } from 'nanoid'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { userSelectors } from '../../../data/store/UserStore'
import LoanSummaryBox from '../components/LoanSummary/LoanSummaryBox'
import WorksheetDecisionDialog from '../components/LoanSummary/WorksheetDecisionDialog'
import WorksheetDecisionSection from '../components/LoanSummary/WorksheetDecisionSection'
import TransactionSection from '../components/Transaction/TransactionSection'
import { useDialogDecisionDefaultValue } from '../viewWorksheet-hooks'
import { canComputeFunding, getEmptyComputedFundingDto } from '../viewWorksheet-selectors'

const IfXpressWorksheetViewPage = () => {
  const { t } = useTranslation()
  const theme = useTheme()

  const [currentOpenDialog, setCurrentOpenDialog] = useState<string>('')

  const dispatchEffect = useSideEffect()

  const dispatch = useAppDispatch()
  const isLoading = useAppSelector(creditSelectors.isLoadingCreditApplication)
  const creditApplication = useAppSelector(creditSelectors.getCurrent)
  const error = useAppSelector(appSelectors.getBusinessError)
  const privateMessages = useAppSelector(messageSelectors.getPrivateMessages)
  const commonNormsMessage: NormsMessage[] = useAppSelector(creditSelectors.getCurrentCreditApplicationSpecificNorms)
  const ifXpressWorksheet = useAppSelector(ifXpressWorksheetSelectors.getCurrent)
  const computedInfo = useAppSelector(ifXpressWorksheetSelectors.getComputedInfo)
  const activePaymentPlan = useAppSelector(creditSelectors.getMerchantPaymentPlan)
  const financingConfig = useAppSelector((state: TRootState) =>
    appSelectors.getFinancingConfig(state, creditApplication?.financingProgramId),
  )

  const availableCreditData = useAppSelector(availableCreditSelectors.get)
  const availableCredit = useAppSelector(availableCreditSelectors.getAvailableCredit)
  const creditInProgress = useAppSelector(availableCreditSelectors.getCreditInProgress)

  const totalObligation = useAppSelector(ifXpressWorksheetSelectors.getTotalObligation)
  const user = useAppSelector(userSelectors.getUser)
  const worksheetNormWarningMessages = useAppSelector(ifXpressWorksheetSelectors.getCurrentWorksheetNormWarningMessages)

  const areAllTasksRequiredForCVTCompleted = useAppSelector(creditSelectors.areAllTasksRequiredForCVTCompleted)
  const areAllDocumentsRequiredForCVTApproved = useAppSelector(documentSelectors.areAllDocumentsRequiredForCVT)
  const confirmAllIncomesStatus = useAppSelector(creditSelectors.getConfirmAllIncomesStatus)
  const finalDecisionIsPending = useAppSelector(creditSelectors.isFinalDecisionPending)

  const dialogDecisionDefaultValue = useDialogDecisionDefaultValue(
    creditApplication!,
    ifXpressWorksheet!,
    areAllTasksRequiredForCVTCompleted,
    areAllDocumentsRequiredForCVTApproved,
    confirmAllIncomesStatus!,
  )

  const editDisabled = creditApplication?.editLocked === true || finalDecisionIsPending
  const planName = creditApplication?.normsCreditDecision?.planName ?? EPlanType.Default

  const breadCrumbs = useMemo(
    () => [
      { path: '/', label: t('breadcrumbs.home') },
      { path: '/Applications/browse', label: t('breadcrumbs.creditApplication') },
      {
        path: `/Applications/${EFinancingProgram.IfXpress}/${creditApplication?.id}/view`,
        label: t('breadcrumbs.application').concat(` #${creditApplication?.referenceNumber}`),
      },
      { path: '#', label: t('worksheet.financing') },
    ],
    [creditApplication, t],
  )

  const EditDecisionDialogOpen = 'EditDecisionDialogOpen'

  useEffect(() => {
    if (creditApplication && ifXpressWorksheet && Object.keys(computedInfo).length === 0) {
      const interestRate = creditApplication.finalCreditDecision.interestRate ?? 0
      const hasCoApplicant = creditApplication.coApplicant !== null
      const stateIso = creditApplication?.applicant.currentAddress.stateIso
      const requestId = nanoid()

      const computeDto: FundingInfoDto = {
        financingProgramId: creditApplication.financingProgramId,
        amountRequested: ifXpressWorksheet.amountRequested ?? 0,
        includeInsurance: ifXpressWorksheet.includeInsurance,
        paymentFrequency: ifXpressWorksheet.paymentFrequency,
        paymentPlanId: ifXpressWorksheet.paymentPlanId,
        term: ifXpressWorksheet.term,
        interestRate,
        hasCoApplicant,
        stateIso: stateIso,
        deliveryOn: ifXpressWorksheet.deliveryOn,
        firstPaymentOn: ifXpressWorksheet.firstPaymentOn,
        requestId,
        creditApplicationId: creditApplication.id,
        merchantId: creditApplication.merchantId,
        insuranceCompanyId: ifXpressWorksheet.insuranceCompanyId,
      }

      if (canComputeFunding(computeDto, financingConfig.minimumLoanAmount)) {
        dispatchEffect(ifXpressWorksheetEffects.computeIfXpressFunding(computeDto)).catch(reportErrorToConsole)
      } else {
        const emptyComputedDto = getEmptyComputedFundingDto()

        dispatch(ifXpressWorksheetActions.setFundingComputed(emptyComputedDto))
      }
    }
  }, [ifXpressWorksheet, computedInfo, creditApplication, dispatchEffect, dispatch, financingConfig.minimumLoanAmount])

  // callbacks
  const closeDialogs = useCallback(() => {
    setCurrentOpenDialog('')
  }, [])

  const handleSaveDecision = useCallback(
    async (data: WorksheetDecision) => {
      if (ifXpressWorksheet) {
        const updatedFinalDecision: IfXpressWorksheetDecisionDTO = {
          creditApplicationId: ifXpressWorksheet.creditApplicationId,
          decision: data.decision,
          otherReason: data.otherReason,
          versionTag: ifXpressWorksheet.versionTag,
        } as IfXpressWorksheetDecisionDTO

        if (data.decision === EWorksheetDecision.ApprovedWithCondition) {
          const fundingThresholdExtend: BaseWorksheetFundingThresholdExtend = {
            maxPmtAmount: data.maxPmtAmount,
            maxTotalAmountFinanced: data.maxTotalAmountFinanced,
            maxTermDuration: data.maxTermDuration,
          }
          updatedFinalDecision.fundingThresholdExtend = fundingThresholdExtend
        }

        await dispatchEffect(
          ifXpressWorksheetEffects.updateDecision({
            ...updatedFinalDecision,
            creditApplicationId: creditApplication?.id,
          }),
        ).then(async () => {
          await dispatchEffect(fullCreditApplicationEffects.getById(creditApplication!.id, EFinancingProgram.IfXpress))
          closeDialogs()
        })
      }
    },
    [ifXpressWorksheet, creditApplication, closeDialogs, dispatchEffect],
  )

  const handleDeletingPrivateMessage = async (message: CreditApplicationMessage) => {
    if (creditApplication?.id) {
      await dispatchEffect(messageEffects.deleteMessage(message, creditApplication.financingProgramId))
    }
  }

  return (
    <div>
      <PageSpinner isLoading={isLoading} />
      {!isLoading && ifXpressWorksheet && creditApplication && (
        <>
          <Breadcrumb trees={breadCrumbs} />
          <Stack>
            <PageError errors={error} />
            {finalDecisionIsPending && <PendingMessage />}
            <Stack direction="row" spacing={2} justifyContent="space-between">
              <InputTextField
                value={t(`EPlanType.${planName}`, { ns: 'enum' })}
                label={t('worksheetCommon.program')}
                disabled
              />
              <InputTextField
                label={t('worksheetCommon.creditLimit')}
                disabled
                value={creditApplication?.finalCreditDecision?.maxAmountFinanced ?? 0}
              />
              <AvailableCreditTooltip availableCreditData={availableCreditData}>
                <div style={{ position: 'relative' }}>
                  <InputTextField label={t('worksheetCommon.creditInProgress')} disabled value={creditInProgress} />
                  {creditInProgress !== 0 && (
                    <ErrorOutline
                      sx={{
                        position: 'absolute',
                        right: '10px',
                        top: '50%',
                        transform: 'translateY(-50%)',
                        color: theme.palette.error.main,
                        fontSize: '1.5rem',
                      }}
                    />
                  )}
                </div>
              </AvailableCreditTooltip>

              <InputTextField label={t('worksheetCommon.availableCredit')} disabled value={availableCredit} />
            </Stack>
            {commonNormsMessage?.length > 0 && (
              <WarningMessageAccordion
                applicantCreditWarnings={null}
                coapplicantCreditWarnings={null}
                commonCreditWarnings={commonNormsMessage}
                worksheetWarnings={null}
                expanded={false}
              />
            )}

            <PrivateMessage
              messages={privateMessages}
              handleDeletingPrivateMessage={handleDeletingPrivateMessage}
              currentUser={user}
              creditAppCreationDate={creditApplication.createdOn}
            />
            <Grid container textAlign="center" justifyContent="space-around" p={4}>
              <Grid
                border="1px solid"
                borderRadius={2}
                p={4}
                size={{
                  xs: 12,
                  md: 4,
                }}
              >
                <LoanSummaryBox
                  worksheet={ifXpressWorksheet}
                  computedInfo={computedInfo}
                  merchantPaymentPlan={activePaymentPlan ?? null}
                />
              </Grid>
              <Grid
                border="1px solid"
                borderRadius={2}
                p={4}
                size={{
                  xs: 12,
                  md: 4,
                }}
              >
                <WorksheetDecisionSection
                  worksheet={ifXpressWorksheet}
                  onClick={() => setCurrentOpenDialog(EditDecisionDialogOpen)}
                  editDisabled={editDisabled}
                  userCanEditWorksheet={user?.rights.canEditWorksheet === true}
                />
              </Grid>
            </Grid>

            <TransactionSection
              worksheet={ifXpressWorksheet}
              creditApplication={creditApplication}
              totalObligation={totalObligation}
              merchantPaymentPlan={activePaymentPlan ?? null}
              productsPlans={[]}
            />
          </Stack>

          <WorksheetDecisionDialog
            open={currentOpenDialog === EditDecisionDialogOpen}
            title={t('worksheet.decision')}
            onCancel={closeDialogs}
            onConfirm={handleSaveDecision}
            worksheetWarnings={worksheetNormWarningMessages}
            defaultValue={dialogDecisionDefaultValue}
            worksheet={ifXpressWorksheet}
            creditapplication={creditApplication}
          />
        </>
      )}
    </div>
  )
}

export default IfXpressWorksheetViewPage
